import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal } from "@fawkes/ui";

import { useAiTegels } from "./schermbouwer/AiTegels";

// ── Dashboardtegels: aansluitend rooster en een eigen indeling (Maarten, 2026-10-12) ───────────────
//
// Twee dingen:
//  1. Geen gaten. Het dashboard was een gewoon raster: de hoogste tegel van een rij bepaalde de hoogte van de hele
//     rij, dus naast een lange "Acties & meldingen" stond een halve schermhoogte leeg. Nu meet elke tegel zijn eigen
//     hoogte en neemt hij precies zoveel smalle rasterrijen in; de volgende tegel sluit er direct onder aan.
//     De tegels blijven daarbij op hun plek in de DOM (alleen `order` en de rijhoogte veranderen), zodat er niets
//     opnieuw wordt opgebouwd of opnieuw gaat laden.
//  2. Eigen indeling. Elke gebruiker kiest welke tegels aan staan en in welke volgorde; dat wordt per gebruiker
//     bewaard (`dashboard/tegels`). Een tegel die er later bij komt staat achteraan en aan.

/** Alle tegels van het dashboard, in de standaardvolgorde. `asiel`: alleen als de asielmodule aan staat. */
export const DASHBOARD_TEGELS: { code: string; titel: string; asiel?: boolean }[] = [
  { code: "operatie", titel: "dashboard.section.operation", asiel: true },
  { code: "locaties", titel: "dashboard.chart.occupancy", asiel: true },
  { code: "entingen", titel: "dashboard.vacc.title", asiel: true },
  { code: "gewicht", titel: "dashboard.gewicht.title", asiel: true },
  { code: "acties", titel: "dashboard.section.actions" },
  { code: "berichten", titel: "berichten.titel" },
  { code: "vandaag", titel: "dashboard.section.today" },
  { code: "sla", titel: "dashboard.section.sla" },
];

export interface TegelKeuze { code: string; zichtbaar: boolean }
export interface DashboardIndeling {
  /** De plek van een tegel; een onbekende code komt achteraan. */
  volgorde: (code: string) => number;
  zichtbaar: (code: string) => boolean;
  /** De volledige lijst in de volgorde van de gebruiker, voor het venster. */
  lijst: TegelKeuze[];
  aangepast: boolean;
  herlaad: () => void;
}

/**
 * De bewaarde keuze aangevuld met wat het scherm kent: onbekende codes eruit, nieuwe tegels achteraan
 * en aan.
 *
 * <p>`extra` zijn de tegels die niet in deze vaste lijst staan maar er wel horen: gepubliceerde
 * ontwerpen uit de AI-schermbouwer. Zonder die ingang valt zo'n tegel hier stil weg, en dan drukt een
 * gebruiker op Publiceren, krijgt een bevestiging, en ziet daarna nergens iets. Dat is precies wat er
 * op het relatiescherm misging toen een nieuwe tegel niet in de vaste sleutellijst stond.
 */
export function voegSamen(bewaard: TegelKeuze[], extra: string[] = []): TegelKeuze[] {
  const bekend = new Set([...DASHBOARD_TEGELS.map((x) => x.code), ...extra]);
  const uit: TegelKeuze[] = [];
  for (const b of bewaard) {
    if (bekend.has(b.code) && !uit.some((x) => x.code === b.code)) uit.push({ code: b.code, zichtbaar: b.zichtbaar !== false });
  }
  for (const d of DASHBOARD_TEGELS) {
    if (!uit.some((x) => x.code === d.code)) uit.push({ code: d.code, zichtbaar: true });
  }
  for (const code of extra) {
    if (!uit.some((x) => x.code === code)) uit.push({ code, zichtbaar: true });
  }
  return uit;
}

export function useDashboardIndeling(): DashboardIndeling {
  const { data, refetch } = useCustom<{ tegels?: TegelKeuze[] }>({ url: "dashboard/tegels", method: "get" });
  const bewaard = Array.isArray(data?.data?.tegels) ? data!.data.tegels! : [];
  // Gepubliceerde tegels uit de schermbouwer horen er net zo goed bij als de ingebouwde.
  const aiTegels = useAiTegels();
  const aiCodes = useMemo(() => aiTegels.map((t) => t.tegelCode), [aiTegels]);
  const lijst = useMemo(() => voegSamen(bewaard, aiCodes), [bewaard, aiCodes]);
  return {
    lijst,
    aangepast: bewaard.length > 0,
    volgorde: (code) => { const i = lijst.findIndex((x) => x.code === code); return i < 0 ? lijst.length : i; },
    zichtbaar: (code) => lijst.find((x) => x.code === code)?.zichtbaar !== false,
    herlaad: () => { void refetch(); },
  };
}

const RIJ = 4; // px, gelijk aan grid-auto-rows in Dashboard.css
const TUSSEN = 18; // px, gelijk aan --fcc-gap

/**
 * Eén tegel in het rooster. Neemt zoveel rasterrijen als zijn inhoud hoog is, zodat de volgende tegel er direct
 * onder aansluit; `breed` is twee kolommen. Staat de tegel uit, dan wordt hij niet getoond.
 */
export function DashboardTegel({ code, breed, indeling, children }: {
  code: string; breed?: boolean; indeling: DashboardIndeling; children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [rijen, setRijen] = useState<number | null>(null);
  const aan = indeling.zichtbaar(code);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const meet = () => {
      const hoogte = el.offsetHeight;
      setRijen(hoogte > 0 ? Math.ceil((hoogte + TUSSEN) / RIJ) : null);
    };
    meet();
    if (typeof ResizeObserver === "undefined") return undefined;
    const kijker = new ResizeObserver(meet);
    kijker.observe(el);
    return () => kijker.disconnect();
  }, [aan]);
  if (!aan) return null;
  return (
    <div className={`fcc-tegel${breed ? " fcc-tegel--breed" : ""}`} data-tegel={code}
      style={{ order: indeling.volgorde(code), ...(rijen ? { gridRowEnd: `span ${rijen}` } : {}) }}>
      <div ref={ref}>{children}</div>
    </div>
  );
}

/** Het venster "Dashboard aanpassen": tegels aan- en uitzetten en van plek wisselen. */
export function DashboardAanpassen({ indeling, asiel, onSluit }: { indeling: DashboardIndeling; asiel: boolean; onSluit: () => void }) {
  const t = useTranslate();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [lijst, setLijst] = useState<TegelKeuze[]>(indeling.lijst);
  const [fout, setFout] = useState<string | null>(null);
  const titelVan = (code: string) => t(DASHBOARD_TEGELS.find((x) => x.code === code)?.titel ?? code);
  // Tegels van een module die uit staat blijven in de lijst bewaard, maar je ziet ze hier niet.
  const toonbaar = (code: string) => asiel || !DASHBOARD_TEGELS.find((x) => x.code === code)?.asiel;
  const zichtbareRijen = lijst.filter((x) => toonbaar(x.code));

  const verplaats = (code: string, stap: -1 | 1) => setLijst((oud) => {
    const zicht = oud.filter((x) => toonbaar(x.code));
    const i = zicht.findIndex((x) => x.code === code);
    const buur = zicht[i + stap];
    if (i < 0 || !buur) return oud;
    const a = oud.findIndex((x) => x.code === code);
    const b = oud.findIndex((x) => x.code === buur.code);
    const nieuw = [...oud];
    [nieuw[a], nieuw[b]] = [nieuw[b], nieuw[a]];
    return nieuw;
  });
  const wissel = (code: string) => setLijst((oud) => oud.map((x) => (x.code === code ? { ...x, zichtbaar: !x.zichtbaar } : x)));
  const bewaar = (tegels: TegelKeuze[]) => {
    setFout(null);
    mutate({ url: "dashboard/tegels", method: "put", values: { tegels }, successNotification: false, errorNotification: false }, {
      onSuccess: () => { indeling.herlaad(); onSluit(); },
      onError: () => setFout(t("dashboard.aanpassen.mislukt")),
    });
  };

  return (
    <Modal open title={t("dashboard.aanpassen.titel")} subtitle={t("dashboard.aanpassen.uitleg")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={() => bewaar([])} disabled={bezig}>{t("dashboard.aanpassen.standaard")}</Button>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={() => bewaar(lijst)} disabled={bezig}>{t("common.save")}</Button>
        </>
      }>
      <ul className="dta-lijst">
        {zichtbareRijen.map((x, i) => (
          <li key={x.code} className={x.zichtbaar ? "" : "dta-uit"}>
            <label className="dta-keuze">
              <input type="checkbox" checked={x.zichtbaar} onChange={() => wissel(x.code)} />
              <span>{titelVan(x.code)}</span>
            </label>
            <span className="dta-pijlen">
              <button type="button" disabled={i === 0} onClick={() => verplaats(x.code, -1)}
                aria-label={t("dashboard.aanpassen.omhoog", { args: [titelVan(x.code)] })} title={t("dashboard.aanpassen.omhoog", { args: [titelVan(x.code)] })}>↑</button>
              <button type="button" disabled={i === zichtbareRijen.length - 1} onClick={() => verplaats(x.code, 1)}
                aria-label={t("dashboard.aanpassen.omlaag", { args: [titelVan(x.code)] })} title={t("dashboard.aanpassen.omlaag", { args: [titelVan(x.code)] })}>↓</button>
            </span>
          </li>
        ))}
      </ul>
      {fout && <p className="fawkes-error" role="alert">{fout}</p>}
    </Modal>
  );
}
